import { Modal, Typography, Button } from "@trimble/modus-cmx";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
type ConfirmationDialogProps = {
  open: boolean;
  title?: string;
  message: string;
  onClose?: () => void;
  onConfirm?: () => void;
  cancelButtonText?: string;
  confirmButtonText?: string;
  /** Overrides default `confirm-button` when multiple confirmation dialogs exist in the tree */
  confirmButtonTestId?: string;
  isLoading?: boolean;
  customContent?: ReactNode;
  singleButton?: boolean;
  showClose?: boolean;
};

export default function ConfirmationDialog({
  open,
  title,
  message,
  onClose,
  onConfirm,
  cancelButtonText,
  confirmButtonText,
  confirmButtonTestId = "confirm-button",
  isLoading,
  customContent,
  singleButton = false,
  showClose = true,
}: ConfirmationDialogProps) {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onClose={onClose}
      modalId="confirmation-dialog"
      data-testid="confirmation-dialog"
      showClose={showClose && !isLoading}
      backdrop="static"
      width={636}
    >
      <div slot="header">
        <Typography
          hierarchy="h4"
          weight="bold"
          size="lg"
          data-testid="confirmation-dialog-title"
        >
          {title}
        </Typography>
      </div>
      <div slot="content" style={{ textAlign: "left" }}>
        <Typography
          hierarchy="h5"
          customClass="modus-wc-typography-override"
          weight="normal"
          data-testid="confirmation-dialog-message"
          className="white-space-pre-line"
        >
          {message}
        </Typography>
        {customContent}
      </div>
      <div
        slot="footer"
        style={{ display: "flex", gap: "16px", justifyContent: "flex-end" }}
      >
        {!singleButton && (
          <Button
            onButtonClick={onClose}
            size="sm"
            color="neutral"
            variant="outlined"
            data-testid="cancel-button"
            disabled={isLoading}
          >
            {cancelButtonText ??
              t("contract_summary.delete_confirmation_dialog.cancel")}
          </Button>
        )}
        {onConfirm && (
          <Button
            onButtonClick={onConfirm}
            size="sm"
            color="primary"
            shape="rectangle"
            variant="filled"
            data-testid={confirmButtonTestId}
            disabled={isLoading}
            loading={isLoading}
          >
            {confirmButtonText ??
              t("contract_summary.delete_confirmation_dialog.yes")}
          </Button>
        )}
      </div>
    </Modal>
  );
}
